import { useRef, useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { submitEstimate } from "@/lib/estimates.functions";
import { HONEYPOT_FIELD } from "@/lib/spam-protection";
import { track } from "@/lib/analytics/track";

const schema = z.object({
  name: z.string().trim().min(1, "Required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  phone: z.string().trim().min(7, "Required").max(30),
  projectType: z.string().min(1),
  city: z.string().trim().min(1, "Required").max(100),
  details: z.string().trim().max(1000).optional().default(""),
  preferredContact: z.string().min(1).default("Email"),
});

const PROJECT_TYPES = [
  "Residential Interior",
  "Residential Exterior",
  "Commercial Property",
  "Cabinet Refinishing",
  "Staining & Finishing",
  "Other",
];

function mapProjectType(t: string):
  "interior" | "exterior" | "cabinets" | "commercial" {
  if (t === "Residential Exterior") return "exterior";
  if (t === "Commercial Property") return "commercial";
  if (t === "Cabinet Refinishing") return "cabinets";
  return "interior";
}

export function EstimateForm({ variant = "card" }: { variant?: "card" | "page" }) {
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const startedAtRef = useRef<number>(Date.now());
  const submit = useServerFn(submitEstimate);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const parsed = schema.safeParse(Object.fromEntries(fd));
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        next[issue.path[0] as string] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    setSubmitting(true);
    setSubmitError(null);
    try {
      const v = parsed.data;
      const notes =
        `Source: Contact form\nPreferred contact: ${v.preferredContact}\nProject type: ${v.projectType}` +
        (v.details ? `\n\n${v.details}` : "");
      await submit({
        data: {
          name: v.name,
          email: v.email,
          phone: v.phone,
          address: null,
          city: v.city,
          projectType: mapProjectType(v.projectType),
          rooms: [],
          surfaces: [],
          surfaceCondition: "good",
          repairsNeeded: null,
          numberOfCoats: 2,
          paintQuality: "premium",
          timeline: null,
          notes,
          studioProjectId: null,
          studioVersionId: null,
          designImageUrl: null,
          selectedColors: [],
          favoriteColors: [],
          anonToken: null,
          honeypot: (fd.get(HONEYPOT_FIELD) as string | null) ?? null,
          formStartedAt: startedAtRef.current,
          turnstileToken: null,
        },
      });
      track.funnel("direct_estimate", "estimate_submitted", 5, {
        source: "contact_form",
        project_type: v.projectType,
      });
      track.event("estimate_submitted", { source: "contact_form" });
      setSubmitted(true);
    } catch (err) {
      setSubmitError((err as Error).message || "Could not send your request. Please call us at (608) 886-4598.");
    } finally {
      setSubmitting(false);
    }
  };

  const shell =
    "relative scroll-mt-24 rounded-[2rem] p-6 sm:p-8 lg:p-10 bg-white/[0.03] border border-white/10 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] overflow-hidden";

  if (submitted) {
    return (
      <div id="estimate" className={shell + " text-center"}>
        <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
        <div className="size-14 mx-auto rounded-full bg-amber-400/15 ring-1 ring-amber-300/30 grid place-items-center mb-5">
          <span className="text-amber-200 text-2xl">✓</span>
        </div>
        <h3 className="font-serif text-2xl text-white mb-3">Estimate request received</h3>
        <p className="text-sm text-zinc-400 max-w-sm mx-auto">
          Thank you. A member of our team will reach out within 24 business hours to schedule your on-site consultation.
        </p>
      </div>
    );
  }

  const isPage = variant === "page";
  return (
    <div id="estimate" className={shell}>
      <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full bg-yellow-400/10 blur-3xl pointer-events-none" />
      <div className="relative">
      <h3 className="font-serif text-2xl text-white mb-2">Request a Free Estimate</h3>
      <p className="text-sm text-zinc-400 mb-6">
        Schedule your consultation before the next project window fills.
      </p>
      <form className="space-y-3.5" onSubmit={onSubmit} noValidate>
        {/* Honeypot — hidden from humans, visible to bots */}
        <input
          type="text"
          name={HONEYPOT_FIELD}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="hidden"
          defaultValue=""
        />
        <Field name="name" placeholder="Full Name" autoComplete="name" error={errors.name} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Field name="email" label="Email Address" type="email" placeholder="Email Address" autoComplete="email" inputMode="email" error={errors.email} />
          <Field name="phone" label="Phone" type="tel" placeholder="Phone" autoComplete="tel" inputMode="tel" error={errors.phone} />
        </div>
        <label htmlFor="estimate-project-type" className="sr-only">Project type</label>
        <select
          id="estimate-project-type"
          name="projectType"
          defaultValue={PROJECT_TYPES[0]}
          className="w-full px-4 py-3.5 bg-white/[0.04] border border-white/10 rounded-xl text-base sm:text-sm text-zinc-200 focus:outline-none focus:border-amber-300/50 transition-colors min-h-[48px]"
        >
          {PROJECT_TYPES.map((p) => (
            <option key={p} value={p} className="bg-zinc-900 text-zinc-100">{p}</option>
          ))}
        </select>
        <Field name="city" label="City" placeholder="City (e.g. Madison, Brookfield)" autoComplete="address-level2" error={errors.city} />
        <label htmlFor="estimate-details" className="sr-only">Project description</label>
        <textarea
          id="estimate-details"
          name="details"
          placeholder="Brief project description"
          rows={isPage ? 5 : 2}
          className="w-full px-4 py-3 bg-white/[0.04] border border-white/10 rounded-xl text-base sm:text-sm text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:border-amber-300/50 transition-colors resize-none"
        />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-zinc-400">
          <span className="text-xs text-zinc-500">Preferred contact:</span>
          <label className="flex items-center gap-2 min-h-[32px]"><input type="radio" name="preferredContact" value="Email" defaultChecked /> Email</label>
          <label className="flex items-center gap-2 min-h-[32px]"><input type="radio" name="preferredContact" value="Phone" /> Phone</label>
          <label className="flex items-center gap-2 min-h-[32px]"><input type="radio" name="preferredContact" value="Text" /> Text</label>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-bold text-sm hover:shadow-[0_0_40px_rgba(234,179,8,0.45)] transition-all min-h-[52px] disabled:opacity-60"
        >
          {submitting ? "Sending…" : "Request My Free Estimate"}
        </button>
        {submitError && (
          <p className="mt-2 text-xs text-red-300 text-center">{submitError}</p>
        )}
      </form>
      <p className="mt-4 text-[10px] text-zinc-500 text-center uppercase tracking-[0.2em]">
        Private &middot; Reply within 24 hours &middot; Estimate range only — confirmed on site
      </p>
      </div>
    </div>
  );
}

function Field({
  name,
  label,
  type = "text",
  placeholder,
  autoComplete,
  inputMode,
  error,
}: {
  name: string;
  label?: string;
  type?: string;
  placeholder: string;
  autoComplete?: string;
  inputMode?: "text" | "email" | "tel" | "numeric" | "decimal" | "search" | "url" | "none";
  error?: string;
}) {
  const id = `estimate-${name}`;
  const labelText = label ?? placeholder;
  return (
    <div>
      <label htmlFor={id} className="sr-only">{labelText}</label>
      <input
        id={id}
        name={name}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        className="w-full px-4 py-3.5 bg-white/[0.04] border border-white/10 rounded-xl text-base sm:text-sm text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:border-amber-300/50 transition-colors min-h-[48px]"
      />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}